<template>
  <div class="card" style="max-width: 700px; margin: auto">
    <div class="card-content">
      <p class="title">
        {{ $t("signUpNewOrganization") }}
      </p>
      <form ref="form" action="onSubmit">
        <b-field :label="$t('legalName')">
          <b-input v-model="formData.legalName" required />
        </b-field>
        <b-field :label="$t('legalEmail')">
          <b-input v-model="formData.legalEmail" required type="email" />
        </b-field>
        <b-field :label="$t('telephoneNumber')" />
        <PhoneNumberPicker v-model="formData.telephoneNumber" :required="formData.mobileNumber ? false : true" />
        <b-field :label="$t('mobileNumber')" />
        <PhoneNumberPicker v-model="formData.mobileNumber" :required="formData.telephoneNumber ? false : true" />
        <b-field :label="$t('legalStreetName')">
          <b-input v-model="formData.legalStreetName" required />
        </b-field>

        <b-field :label="$t('legalHouseNo')">
          <b-input v-model="formData.legalHouseNo" required />
        </b-field>

        <b-field :label="$t('legalZipCode')">
          <b-input v-model="formData.legalZipCode" required />
        </b-field>

        <b-field :label="$t('legalCity')">
          <b-input v-model="formData.legalCity" required />
        </b-field>

        <CountryPicker
          v-model="formData.legalCountry"
          class="mb-3"
          :is-loading="isLoading"
          @checkValidity="checkValidity"
        />

        <b-field v-if="isVatCountry" :label="$t('vatId')">
          <b-input v-model="formData.vatId" :placeholder="isGermany ? $t('optional') : ''" />
        </b-field>
        <b-field>
          <b-checkbox v-if="!isVatCountry" v-model="formData.checkbox" :required="!isVatCountry || isGermany">
            {{ $t("confirmNoVat") }}
          </b-checkbox>
        </b-field>
        <b-field :label="$t('billingCurrency')">
          <CurrencyPicker v-model="formData.currency" />
        </b-field>
        <div class="mt-4">
          <b-button type="is-primary" :disabled="isDisabled" @click.prevent="onSubmit">
            {{ $t("signUp") }}
          </b-button>
        </div>
      </form>
    </div>
    <b-loading :active.sync="isLoading" :is-full-page="false" :can-cancel="false" />
  </div>
</template>

<script>
import { functions, auth } from "@/firebase";
import { VAT_COUNTRY_LIST } from "@/env";
import CurrencyPicker from "@/components/CurrencyPicker";
import CountryPicker from "@/components/CountryPicker";
import PhoneNumberPicker from "@/components/PhoneNumberPicker";
import { mapState } from "vuex";

export default {
  name: "RegistrationCardOrganization",
  components: {
    CurrencyPicker,
    CountryPicker,
    PhoneNumberPicker,
  },
  data() {
    return {
      isDisabled: true,
      isLoading: false,
      isVatCountry: true,
      isGermany: false,

      formData: {
        vatId: "",
        legalCity: "",
        legalCountry: "",
        legalName: "",
        legalStreetName: "",
        legalHouseNo: "",
        legalZipCode: "",
        legalEmail: "",
        telephoneNumber: "",
        mobileNumber: "",
        currency: "EUR",
        checkbox: false,
      },
      ref: null,
      countryInput: "",
    };
  },
  computed: {
    ...mapState({
      subscriptionPlan: (state) => state.subscriptionPlan,
      subscriptionTrial: (state) => state.subscriptionTrial,
      routeTosubsPage: (state) => state.routeTosubsPage,
    }),
    // returns true if vat requirement is met
    vatRequirement() {
      if (this.isVatCountry) {
        if (this.isGermany) {
          // vat id is optional in germany
          return true;
        }
        if (this.formData.vatId !== "") {
          // only true if vat id is given
          return true;
        }
        return false;
      } else {
        if (this.formData.checkbox) {
          // check if checked
          return true;
        }
        return false;
      }
    },
  },
  watch: {
    // this works only with html5 validation
    // keep the submit button disabled as long as the form is invalid
    formData: {
      deep: true,
      handler() {
        this.checkValidity();
      },
    },
    isVatCountry() {
      console.log(this.isVatCountry && !this.isGermany);
      this.isDisabled = true;
      if (this.isVatCountry) {
        this.formData.vatId = "";
      } else {
        this.formData.checkbox = false;
      }
    },
  },
  created() {
    // force user to create account if not logged in
    auth.onAuthStateChanged(async (user) => {
      if (!user) {
        this.$router.push({ name: "Register", params: { target: "user" } });
      }
    });
  },
  methods: {
    checkValidity() {
      if (this.formData.legalCountry.toUpperCase().length != 0) {
        this.isVatCountry = VAT_COUNTRY_LIST.includes(this.formData.legalCountry.toUpperCase());
      }
      if (this.formData.legalCountry === "de") {
        this.isGermany = true;
      } else {
        this.isGermany = false;
      }
      const valid = this.$refs.form.checkValidity() && this.vatRequirement && this.formData.legalCountry != "";
      this.formData.vatId = this.formData.vatId.replace(/\s/g, "");
      this.isDisabled = !valid;
    },
    async onSubmit() {
      console.log(this.isVatCountry && !this.isGermany);

      console.log(this.isVatCountry);

      if (this.isDisabled) return;

      this.isDisabled = true;
      this.isLoading = true;
      if (localStorage.ref) {
        this.ref = localStorage.ref;
      }

      // check if organization wants to register as agency
      let isAgency = false;
      if (this.$route.params.target == "agency") {
        isAgency = true;
      }

      // create organization
      await functions
        .httpsCallable("CreateOrganization")({
          vatId: this.formData.vatId,
          legalCity: this.formData.legalCity,
          legalCountry: this.formData.legalCountry,
          legalName: this.formData.legalName,
          legalStreetName: this.formData.legalStreetName,
          legalHouseNo: this.formData.legalHouseNo,
          legalZipCode: this.formData.legalZipCode,
          legalEmail: this.formData.legalEmail,
          telephoneNumber: this.formData.telephoneNumber,
          mobileNumber: this.formData.mobileNumber,
          userId: auth.currentUser.uid,
          currency: this.formData.currency,
          ref: this.ref,
          isVatCountry: this.isVatCountry,
          isAgency: isAgency,
        })
        .then(async ({ data }) => {
          let organizationId = data.organizationId;
          this.isLoading = true;
          this.isDisabled = true;

          this.$buefy.toast.open({
            message: this.$t("registrationSucceed"),
            type: "is-success",
            duration: 4000,
          });

          //Setting organizationId to session storage
          sessionStorage.organizationId = organizationId;
          this.$store.dispatch("updateOrganizationId", organizationId);

          this.$store.commit("SET_PERMISSION", organizationId);

          // Check what to load, dashboard or subs page
          if (this.routeTosubsPage && !this.subscriptionTrial) {
            this.$router.push({
              name: "Subscription",
              query: {
                type: this.subscriptionDetails.type,
                plan: this.subscriptionDetails.plan,
                amount: this.subscriptionDetails.amount,
                maxUser: this.subscriptionDetails.maxUser,
                maxLang: this.subscriptionDetails.maxLang,
                billInterval: this.subscriptionDetails.billInterval,
                currency: this.subscriptionDetails.currency,
              },
              params: { organizationId: organizationId },
            });
          }
          if (this.routeTosubsPage && this.subscriptionTrial) {
            this.$router.push({
              name: "Subscription",
              query: {
                type: "trial",
              },
              params: { organizationId: organizationId },
            });
          } else {
            this.$router.push({
              name: "Dashboard",
              params: { organizationId: organizationId },
            });
          }
        })
        .catch((err) => {
          console.log(err.message);
          this.$buefy.toast.open({
            message: this.$t(err.message),
            type: "is-danger",
            duration: 4000,
          });
          this.isLoading = false;
          this.isDisabled = false;
        });
    },
  },
};
</script>
